import type { FC, ReactNode } from 'react'
import type { ClinicActions } from '@/lib/clinic/actions'
import type { ClinicData, ClinicIndex } from '@/lib/clinic/types'

import { toast } from 'sonner'
import { createClinicActions } from '@/lib/clinic/actions'
import { clinicRepository, isClinicStorageKey } from '@/lib/clinic/repository'
import { buildClinicIndex } from '@/lib/clinic/selectors'
import { createEmptyData } from '@/lib/clinic/seed'
import { todayKey } from '@/lib/clinic/datetime'

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react'

type ClinicStatus = 'loading' | 'ready' | 'error'

interface ClinicContextValue {
    status: ClinicStatus
    ready: boolean
    today: string
    data: ClinicData
    index: ClinicIndex
    actions: ClinicActions
    reload: () => void
}

interface ClinicProviderProps {
    children: ReactNode
}

const EMPTY_DATA = createEmptyData()

const ClinicContext = createContext<ClinicContextValue | null>(null)

export const ClinicProvider: FC<ClinicProviderProps> = ({ children }): ReactNode => {
    const [status, setStatus] = useState<ClinicStatus>('loading')
    const [data, setData] = useState<ClinicData>(EMPTY_DATA)
    const [today, setToday] = useState('')
    const dataRef = useRef<ClinicData>(EMPTY_DATA)

    const commit = useCallback((next: ClinicData) => {
        dataRef.current = next
        setData(next)
        setStatus('ready')
    }, [])

    const load = useCallback(() => {
        clinicRepository
            .load()
            .then(commit)
            .catch((error: unknown) => {
                console.error(error)
                setStatus('error')
            })
    }, [commit])

    useEffect(() => {
        load()

        const handleStorage = (event: StorageEvent) => {
            if (event.key && isClinicStorageKey(event.key)) load()
        }

        window.addEventListener('storage', handleStorage)

        return () => window.removeEventListener('storage', handleStorage)
    }, [load])

    useEffect(() => {
        const update = () => setToday(todayKey())

        update()
        const timer = window.setInterval(update, 60_000)

        return () => window.clearInterval(timer)
    }, [])

    const actions = useMemo(() => createClinicActions({
        getData: () => dataRef.current,
        commit,
        repository: clinicRepository,
        onError: message => {
            toast.error(message)
        }
    }), [commit])

    const index = useMemo(() => buildClinicIndex(data), [data])

    const value = useMemo<ClinicContextValue>(() => ({
        status,
        ready: status === 'ready' && today !== '',
        today,
        data,
        index,
        actions,
        reload: load
    }), [status, today, data, index, actions, load])

    return (
        <ClinicContext.Provider value={value}>
            {children}
        </ClinicContext.Provider>
    )
}

export const useClinic = (): ClinicContextValue => {
    const context = useContext(ClinicContext)
    if (!context) throw new Error('useClinic must be used inside ClinicProvider')

    return context
}
